/**
 * ErrorBoundary
 *
 * Wraps the new-ui app's route tree. Catches React render/lifecycle errors
 * that would otherwise blank the whole tree to a white screen, and:
 *   1) Reports the error to the backend (POST /api/errors/report) via
 *      utils/errorReporter so the bug shows up in Admin → Error Logs.
 *   2) Renders a graceful fallback UI with a Reload button.
 *
 * Note: this only catches errors thrown during React rendering / lifecycle.
 * Errors in event handlers, async callbacks, setTimeout, etc. don't reach
 * a boundary — those are caught by the window.onerror /
 * window.onunhandledrejection hooks installed in utils/errorReporter.
 */

import React from 'react';
import { reportError } from '../../utils/errorReporter';
import Button from './Button';

// ErrorBoundary renders above AuthProvider (so it can catch crashes there
// too), so useContext isn't available — mirror the tokenManager key from
// services/api.js. Keep in sync if that changes.
const USER_STORAGE_KEY = 'cs_user';
const ADMIN_ROLES = new Set(['admin', 'super_admin']);

function readUserRoleFromStorage() {
  try {
    const raw =
      (typeof localStorage !== 'undefined' && localStorage.getItem(USER_STORAGE_KEY)) ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(USER_STORAGE_KEY)) ||
      null;
    if (!raw) return null;
    return JSON.parse(raw)?.roleName || null;
  } catch {
    return null;
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    try {
      reportError({
        name: error?.name || 'ReactRenderError',
        message: error?.message,
        stack: error?.stack,
        componentStack: info?.componentStack,
      });
    } catch (_) {
      // Last-ditch silent — we're in the error path.
    }
  }

  handleReload = () => {
    // Full reload to recover from a broken render tree.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV === 'development';
    const isAdmin = ADMIN_ROLES.has(readUserRoleFromStorage());
    // Dev builds see it; logged-in admins see it in any environment.
    const showTechnicalDetails = isDev || isAdmin;
    const { error, info } = this.state;

    return (
      <div
        className="min-h-[var(--app-vh)] w-full flex items-center justify-center px-6"
        style={{ background: '#f4f0e8' }}
      >
        <div className="max-w-[560px] w-full text-center">
          <img
            src="/complyv-logo.svg"
            alt=""
            className="w-14 h-14 mx-auto mb-4 object-contain"
          />
          <div
            className="text-[26px] font-bold leading-none mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b8973a' }}
          >
            Something went wrong
          </div>
          <div className="text-[13px] text-[#7a7060] mb-6">
            The page hit an unexpected error and couldn't render. We've been notified — try reloading.
          </div>

          <Button type="button" size="lg" onClick={this.handleReload}>
            Reload page
          </Button>

          {showTechnicalDetails && error && (
            <details className="mt-6 text-left text-[11px] text-[#7a7060]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <summary className="cursor-pointer mb-2 select-none">
                Dev details
                {!isDev && isAdmin && (
                  <span className="ml-1 text-[#b8973a]">(admin view)</span>
                )}
              </summary>
              <div className="bg-white border rounded-[8px] p-3 overflow-auto max-h-[40vh]" style={{ borderColor: '#e8e2d6' }}>
                <div className="font-bold">{error.name}: {error.message}</div>
                {error.stack && (
                  <pre className="whitespace-pre-wrap mt-2 text-[10.5px]">{error.stack}</pre>
                )}
                {info?.componentStack && (
                  <pre className="whitespace-pre-wrap mt-2 text-[10.5px] text-[#9a564a]">{info.componentStack}</pre>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }
}
